import "./BugReportModal.scss";
import { useState, useRef, useEffect } from "react";
import FlowTextInput from "../../FlowTextInput/FlowTextInput";
import { submitBugReport } from "../../../api/bugReports/bugReports";
import useAuthStore from "../../../store/authStore";

function BugReportModal({ onClose }) {
  const [reportType, setReportType] = useState("bug"); // 'bug' or 'feature'
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const titleInputRef = useRef(null);
  const user = useAuthStore((state) => state.user);
  const token = useAuthStore((state) => state.token);

  // Auto-focus title input when modal opens
  useEffect(() => {
    const timer = setTimeout(() => {
      titleInputRef.current?.focus();
    }, 50);
    return () => clearTimeout(timer);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!title.trim() || !description.trim()) {
      return;
    }

    setIsSubmitting(true);

    try {
      await submitBugReport({
        type: reportType,
        title: title.trim(),
        description: description.trim(),
        userEmail: user?.email || "",
        userName:
          `${user?.firstName || ""} ${user?.lastName || ""}`.trim() ||
          user?.email ||
          "",
        token,
      });

      // Clear form and close modal on success
      setTitle("");
      setDescription("");
      onClose();
    } catch (error) {
      console.error("Failed to submit bug report:", error);
      alert(
        `Failed to submit ${reportType === "bug" ? "bug report" : "feature request"}. Please try again.`
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClear = () => {
    setTitle("");
    setDescription("");
    onClose();
  };

  return (
    <div className="bug-report-modal-content">
      <div className="title">
        <span>Bug and Feature Requests</span>
        <button className="close-button" onClick={onClose}>
          <span className="material-symbols-outlined">close</span>
        </button>
      </div>

      <div className="main-wrapper">
        <div className="content">
          <div className="type-selector">
            <button
              className={`type-button ${reportType === "bug" ? "active" : ""}`}
              onClick={() => setReportType("bug")}
            >
              <span className="material-symbols-outlined">bug_report</span>
              Bug Report
            </button>
            <button
              className={`type-button ${reportType === "feature" ? "active" : ""}`}
              onClick={() => setReportType("feature")}
            >
              <span className="material-symbols-outlined">lightbulb</span>
              Feature Request
            </button>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="title">Title</label>
              <FlowTextInput
                ref={titleInputRef}
                placeholder={`Enter ${reportType === "bug" ? "bug" : "feature"} title...`}
                value={title}
                onChange={setTitle}
                expandable={false}
                icon=""
                className="bug-report-input"
              />
            </div>

            <div className="form-group">
              <label htmlFor="description">Description</label>
              <textarea
                id="description"
                className="form-textarea"
                placeholder={`Describe the ${reportType === "bug" ? "bug you encountered" : "feature you'd like to see"}...`}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={8}
                required
              />
            </div>

            <div className="form-actions">
              <button
                type="button"
                className="action-button clear"
                onClick={handleClear}
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="action-button submit"
                disabled={isSubmitting}
              >
                {isSubmitting
                  ? "Submitting..."
                  : `Submit ${reportType === "bug" ? "Bug" : "Feature"}`}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default BugReportModal;
